import { take } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { getSelectedLocationIds } from '@app/data/helpers/locationHelpers';
import { EChartsOption } from 'echarts';
import { getPestCountChartConfig, PestCountData } from './rongo-pest-count-chart-helpers';
import { RongoReportingData, RongoSourceGroup } from '@app/widgets/helpers/types';
import { withOrganisationBreakdown } from '@app/widgets/helpers/organisation-breakdown';

@Component({
  selector: 'app-rongo-pest-count-chart',
  templateUrl: './rongo-pest-count-chart.component.html',
  styleUrls: ['./rongo-pest-count-chart.component.scss'],
})
export class RongoPestCountChartComponent implements OnInit {
  chartTitle: string = 'Riha Rāwaho - Pests Observed';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-pest-count';
  startDate: any = '';
  endDate: any = '';
  locationIds: string[] = [];
  data: PestCountData | [] = [];
  availableOptions: string[] = [];
  selectedOptions: string[] = [];
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  site: Site | null = null;
  breakdown: RongoSourceGroup<PestCountData>[] | null = null;

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService
  ) {}

  ngOnInit() {
    this.locationIds = getSelectedLocationIds(this.filterService.getFilters().selectedLocations);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }

  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService.getPestCountData(this.startDate, this.endDate, this.locationIds, 'organisation').subscribe(
      (data: RongoReportingData<PestCountData>) => {
        const { config, data: pestCountData, breakdown } = data;
        this.chartTitle = config.title;
        this.chartSubtitle = config.subTitle || '';
        this.data = pestCountData;
        this.breakdown = breakdown?.groups || null;
        this.noData = Object.keys(pestCountData).length === 0;
        this.availableOptions = Object.keys(this.data);
        this.selectedOptions = this.availableOptions.slice(0, 5);
        this.setChartData();
      },
      (error: any) => {
        console.error('Error fetching data:', error);
      }
    );
  };

  setChartData = () => {
    const filterData = (data: PestCountData) =>
      this.selectedOptions.reduce((acc, key) => {
        if (key in data) {
          return { ...acc, [key]: data[key] };
        }
        return acc;
      }, {} as PestCountData);

    this.chartOptions = withOrganisationBreakdown(
      getPestCountChartConfig(filterData(this.data as PestCountData)),
      this.breakdown,
      {
        buildGroupOption: (d) => getPestCountChartConfig(filterData(d)),
        flatten: (d: PestCountData) => Object.entries(d).map(([name, details]) => ({ name, value: details.pestCount })),
      }
    );
  };

  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };

  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const newLocationIds = getSelectedLocationIds(filters.filters.selectedLocations);
    const locationsChanged = newLocationIds.join(',') !== this.locationIds.join(',');
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || locationsChanged || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.locationIds = newLocationIds;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }

  handlePestListChange(event: MatSelectChange): void {
    this.setChartData();
  }
}
